import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronDown, Sparkles } from 'lucide-react';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

export default function WhatsNew() {
  const [data, setData] = useState({ current_version: '', entries: [] });
  const [loading, setLoading] = useState(true);
  const [openIndex, setOpenIndex] = useState(0);

  useEffect(() => {
    api.get('/release-notes')
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6 max-w-3xl" data-testid="whats-new-page">
      <div>
        <h1 className="page-title flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-brand-accent" aria-hidden="true" />
          What&apos;s New
        </h1>
        <p className="text-sm text-fg-muted mt-1">
          Everything we&apos;ve added and improved in TriCorps360, newest first.
        </p>
      </div>

      {loading && (
        <div className="text-sm text-fg-muted" data-testid="whats-new-loading">
          Loading release notes...
        </div>
      )}

      {!loading && data.entries.length === 0 && (
        <div className="stat-card text-sm text-fg-muted" data-testid="whats-new-empty">
          Release notes aren&apos;t available right now. Please try again later.
        </div>
      )}

      <div className="space-y-3">
        {data.entries.map((entry, i) => {
          const isCurrent = entry.version && entry.version === data.current_version;
          const open = openIndex === i;
          return (
            <section
              key={entry.title}
              className="rounded-lg border border-line bg-surface overflow-hidden"
              data-testid={`release-entry-${entry.version || 'earlier'}`}
            >
              <button
                type="button"
                onClick={() => setOpenIndex(open ? -1 : i)}
                aria-expanded={open}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[hsl(var(--hover-overlay))] transition-colors"
                data-testid={`release-toggle-${entry.version || 'earlier'}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-fg">
                      {entry.version ? `Version ${entry.version}` : entry.title}
                    </span>
                    {isCurrent && (
                      <Badge className="bg-success-bg text-success border border-success-border hover:bg-success-bg">
                        You&apos;re on this
                      </Badge>
                    )}
                  </div>
                  {entry.released && (
                    <p className="text-xs text-fg-muted mt-0.5">{formatDate(entry.released)}</p>
                  )}
                </div>
                <ChevronDown
                  className={cn('w-4 h-4 text-fg-muted shrink-0 transition-transform', open && 'rotate-180')}
                  aria-hidden="true"
                />
              </button>

              {open && (
                <div
                  className="px-4 pb-5 pt-1 prose prose-sm dark:prose-invert max-w-none prose-headings:text-fg prose-p:text-fg-secondary prose-li:text-fg-secondary prose-strong:text-fg"
                  data-testid={`release-body-${entry.version || 'earlier'}`}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{entry.body}</ReactMarkdown>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
